import { type UseFormReturn, useFieldArray, useWatch } from "react-hook-form"

import Icon from "@/components/Icon"
import AppCommsPreview from "@/components/incentives/AppCommsPreview"
import {
  type IncentiveFormValues,
  emptyAudience,
  hasAppComms,
  partialWeekWarning,
  ruleSummary,
} from "@/components/incentives/incentiveFormSchema"
import {
  ROUTE_TYPE_GROUPS,
  ROUTE_TYPE_HINTS,
  ROUTE_TYPE_LABELS,
  ROUTE_TYPE_OPTION_LABELS,
} from "@/components/incentives/routeTypeLabels"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { MultiSelect } from "@/components/ui/multi-select"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { ALL_SHERIFF_AREAS } from "@/constants/sheriffOptions"
import {
  APP_COMMS_MAX_LENGTHS,
  type Audience,
  ROUTE_CYCLES,
  useIncentiveAudienceOptions,
} from "@/hooks/useIncentives"
import { cn } from "@/lib/utils"

import { Constants } from "../../../database.types"

// A live incentive takes only its end date and App Comms.
type Editable = "all" | "live" | "none"

type IncentiveFormProps = {
  form: UseFormReturn<IncentiveFormValues>
  editable: Editable
  onSubmit: (values: IncentiveFormValues) => void
  // The incentive this one was copied from, to flag fields still carrying its text.
  copyOf?: IncentiveFormValues
  children?: React.ReactNode
}

const AUDIENCE_FIELDS: { key: keyof Audience; label: string }[] = [
  { key: "areas", label: "Area" },
  { key: "types", label: "Type" },
  { key: "roles", label: "Role" },
  { key: "depots", label: "Depot" },
]

type Option = { value: string; label: string }

const asOptions = (values: readonly string[]): Option[] =>
  values.map((value) => ({ value, label: value }))

// A saved value no longer offered still shows, rather than vanishing from the selection.
const optionsFor = (options: Option[], selected: string[]): Option[] => [
  ...options,
  ...asOptions(selected.filter((value) => !options.some((option) => option.value === value))),
]

const toggled = <T,>(values: T[], value: T, checked: boolean): T[] =>
  checked ? [...values, value] : values.filter((other) => other !== value)

// FormMessage for an error on an audience as a whole, which has no single field to sit under.
const ErrorMessage = ({ message }: { message?: string }) =>
  message ? <p className="text-sm font-medium text-destructive">{message}</p> : null

const SameAsCopyWarning = ({ show }: { show: boolean }) =>
  show ? (
    <p className="text-sm font-medium text-indicators-yellow">
      Same as the incentive you copied. Check it still fits this one.
    </p>
  ) : null

export default function IncentiveForm({
  form,
  editable,
  onSubmit,
  copyOf,
  children,
}: IncentiveFormProps) {
  const locked = editable !== "all"
  const readOnly = editable === "none"
  const { data: audienceOptions } = useIncentiveAudienceOptions()
  const audiences = useFieldArray({ control: form.control, name: "audiences" })
  const values = useWatch({ control: form.control }) as IncentiveFormValues
  const atThreshold = values.unit === "threshold"
  const perDay = values.one_route_per_day === true
  const warning = partialWeekWarning(values)
  const withAppComms = hasAppComms(values.app_comms)

  const choices: Record<keyof Audience, Option[]> = {
    areas: asOptions(ALL_SHERIFF_AREAS),
    types: asOptions(Constants.public.Enums.person_type),
    roles: asOptions(audienceOptions?.roles ?? []),
    depots: audienceOptions?.depots ?? [],
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader className="border-b border-palette-jungleGreen100 pb-4">
            <CardTitle className="text-lg text-palette-jungleGreen900">Details</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={locked} />
                  </FormControl>
                  <SameAsCopyWarning show={!!copyOf && field.value.trim() === copyOf.name.trim()} />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="start_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start date (inclusive)</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} disabled={locked} />
                  </FormControl>
                  <FormDescription className="text-xs leading-relaxed text-palette-jungleGreen600">
                    The first day that counts.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="end_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End date (inclusive)</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} disabled={readOnly} />
                  </FormControl>
                  <FormDescription className="text-xs leading-relaxed text-palette-jungleGreen600">
                    The last day that counts.{" "}
                    {atThreshold && values.period === "whole_run"
                      ? "Required for a whole incentive."
                      : "Optional. Leave it empty and the incentive runs until someone marks it as ended."}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b border-palette-jungleGreen100 pb-4">
            <CardTitle className="text-lg text-palette-jungleGreen900">Eligible Routes</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <FormField
              control={form.control}
              name="route_types"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block pb-2">Route types</FormLabel>
                  <div className="divide-y divide-palette-jungleGreen100">
                    {ROUTE_TYPE_GROUPS.map((group) => (
                      <div key={group.label} className="grid gap-2 py-3 md:grid-cols-[11rem_1fr]">
                        <span className="text-sm font-medium text-palette-jungleGreen900">
                          {group.label}
                        </span>
                        <div className="grid gap-2 md:grid-cols-2">
                          {group.types.map((routeType) => (
                            <FormItem key={routeType} className="flex items-start gap-2 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value.includes(routeType)}
                                  onCheckedChange={(checked) =>
                                    field.onChange(
                                      toggled(field.value, routeType, checked === true),
                                    )
                                  }
                                  disabled={locked}
                                  // The visible text leans on the group label, so "Driver's fault" appears twice.
                                  aria-label={ROUTE_TYPE_LABELS[routeType]}
                                  className="border-palette-jungleGreen500 data-[state=checked]:border-palette-jungleGreen900 mt-0.5"
                                />
                              </FormControl>
                              <FormLabel className="font-normal leading-snug">
                                {ROUTE_TYPE_OPTION_LABELS[routeType]}
                              </FormLabel>
                              <InfoTooltip
                                text={ROUTE_TYPE_HINTS[routeType]}
                                className="mt-0.5 shrink-0"
                              />
                            </FormItem>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <FormDescription className="text-xs leading-relaxed text-palette-jungleGreen600">
                    Every route has exactly one type, from what happened to it rather than what it
                    started as.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-8 border-t border-palette-jungleGreen100 pt-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="route_cycles"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block pb-2">Route cycles</FormLabel>
                    <div className="flex gap-6">
                      {ROUTE_CYCLES.map((cycle) => (
                        <FormItem key={cycle} className="flex items-center gap-2 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value.includes(cycle)}
                              onCheckedChange={(checked) =>
                                field.onChange(toggled(field.value, cycle, checked === true))
                              }
                              disabled={locked}
                              className="border-palette-jungleGreen500 data-[state=checked]:border-palette-jungleGreen900"
                            />
                          </FormControl>
                          <FormLabel className="font-normal">{cycle}</FormLabel>
                        </FormItem>
                      ))}
                    </div>
                    <FormDescription className="text-xs leading-relaxed text-palette-jungleGreen600">
                      The route's own cycle, not the driver's block or an audience's Area such as
                      "Rc1 Deliveries".
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="one_route_per_day"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block pb-2">Routes a day</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value === null ? "" : field.value ? "one" : "every"}
                        onValueChange={(value) => field.onChange(value === "one")}
                        disabled={locked}
                        className="space-y-2"
                      >
                        <FormItem className="flex items-start gap-2 space-y-0">
                          <FormControl>
                            <RadioGroupItem
                              value="every"
                              className="border-palette-jungleGreen500 text-palette-jungleGreen900 data-[state=checked]:border-palette-jungleGreen900 mt-0.5"
                            />
                          </FormControl>
                          <div className="space-y-1">
                            <FormLabel className="font-normal">Every route counts</FormLabel>
                            <FormDescription className="text-xs leading-relaxed text-palette-jungleGreen600">
                              Two routes on one day count as two.
                            </FormDescription>
                          </div>
                        </FormItem>
                        <FormItem className="flex items-start gap-2 space-y-0">
                          <FormControl>
                            <RadioGroupItem
                              value="one"
                              className="border-palette-jungleGreen500 text-palette-jungleGreen900 data-[state=checked]:border-palette-jungleGreen900 mt-0.5"
                            />
                          </FormControl>
                          <div className="space-y-1">
                            <FormLabel className="font-normal">At most one a day</FormLabel>
                            <FormDescription className="text-xs leading-relaxed text-palette-jungleGreen600">
                              Counts the days with a route that counts, e.g. "5 routes on different
                              days".
                            </FormDescription>
                          </div>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b border-palette-jungleGreen100 pb-4">
            <CardTitle className="text-lg text-palette-jungleGreen900">Pay Configuration</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <FormField
              control={form.control}
              name="unit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block pb-2">Pay</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={locked}
                      className="flex gap-6"
                    >
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem
                            value="per_route"
                            className="border-palette-jungleGreen500 text-palette-jungleGreen900 data-[state=checked]:border-palette-jungleGreen900"
                          />
                        </FormControl>
                        <FormLabel className="font-normal">Per route</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem
                            value="threshold"
                            className="border-palette-jungleGreen500 text-palette-jungleGreen900 data-[state=checked]:border-palette-jungleGreen900"
                          />
                        </FormControl>
                        <FormLabel className="font-normal">At a threshold</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {atThreshold && (
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="threshold"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Threshold ({perDay ? "days" : "routes"})</FormLabel>
                      <FormControl>
                        <Input inputMode="numeric" {...field} disabled={locked} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="period"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="block pb-2">Period</FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={locked}
                          className="space-y-1"
                        >
                          <FormItem className="flex items-center gap-2 space-y-0">
                            <FormControl>
                              <RadioGroupItem
                                value="weekly"
                                className="border-palette-jungleGreen500 text-palette-jungleGreen900 data-[state=checked]:border-palette-jungleGreen900"
                              />
                            </FormControl>
                            <FormLabel className="font-normal">Every week (Mon-Sun)</FormLabel>
                            <InfoTooltip text="Pays each week a driver reaches the threshold." />
                          </FormItem>
                          <FormItem className="flex items-center gap-2 space-y-0">
                            <FormControl>
                              <RadioGroupItem
                                value="whole_run"
                                className="border-palette-jungleGreen500 text-palette-jungleGreen900 data-[state=checked]:border-palette-jungleGreen900"
                              />
                            </FormControl>
                            <FormLabel className="font-normal">Whole incentive</FormLabel>
                            <InfoTooltip text="Pays once, in the week the driver reaches the threshold." />
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount</FormLabel>
                  <div className="relative max-w-[200px]">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-palette-jungleGreen600">
                      £
                    </span>
                    <FormControl>
                      <Input inputMode="decimal" {...field} disabled={locked} className="pl-7" />
                    </FormControl>
                  </div>
                  <FormDescription className="text-xs leading-relaxed text-palette-jungleGreen600">
                    {atThreshold
                      ? "Paid once the threshold is reached."
                      : perDay
                        ? "Paid for each day with a route that counts."
                        : "Paid for each route."}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {warning && !locked && (
              <Alert>
                <Icon name="warning" className="h-4 w-4" />
                <AlertDescription>{warning}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2 rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000 p-4">
              <span className="flex items-center gap-1.5 text-xs font-medium text-palette-jungleGreen600">
                <Icon name="infoCircle" className="h-3.5 w-3.5 text-current" />
                Rule preview
              </span>
              <p className="text-sm leading-7 text-palette-jungleGreen900">
                {ruleSummary(values).map((part, index) =>
                  typeof part === "string" ? (
                    part
                  ) : (
                    <strong
                      key={index}
                      className="rounded border border-palette-jungleGreen200 bg-white px-1.5 py-0.5 font-semibold"
                    >
                      {part.value}
                    </strong>
                  ),
                )}
              </p>
            </div>

            <FormField
              control={form.control}
              name="pay_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pay description</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} disabled={locked} className="min-h-24 resize-y" />
                  </FormControl>
                  <FormDescription className="text-xs leading-relaxed text-palette-jungleGreen600">
                    The text that appears on the driver's amendment, e.g. "£150 HPD incentive, 12
                    completed RC1 routes 28 Sep-11 Oct".
                  </FormDescription>
                  <SameAsCopyWarning
                    show={!!copyOf && field.value.trim() === copyOf.pay_description.trim()}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b border-palette-jungleGreen100 pb-4 flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-lg text-palette-jungleGreen900">Audiences</CardTitle>
              <p className="text-xs leading-relaxed text-palette-jungleGreen600 mt-1">
                A driver only needs to be in one audience to count. Leave a field empty to match any
                value.
              </p>
            </div>
            {!locked && (
              <Button
                type="button"
                variant="outline"
                onClick={() => audiences.append(emptyAudience())}
              >
                <Icon name="plus" className="h-4 w-4 text-current" />
                Add audience
              </Button>
            )}
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {audiences.fields.map((audience, index) => (
              <div
                key={audience.id}
                className="space-y-3 rounded-lg border border-palette-jungleGreen100 p-4"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Audience {index + 1}</span>
                  {!locked && audiences.fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => audiences.remove(index)}
                      aria-label={`Remove audience ${index + 1}`}
                    >
                      <Icon name="bin" className="h-4 w-4 text-current" />
                    </Button>
                  )}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {AUDIENCE_FIELDS.map(({ key, label }) => (
                    <FormField
                      key={key}
                      control={form.control}
                      name={`audiences.${index}.${key}`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{label}</FormLabel>
                          <MultiSelect
                            options={optionsFor(choices[key], field.value)}
                            selected={field.value}
                            onChange={field.onChange}
                            placeholder={`Any ${label.toLowerCase()}`}
                            disabled={locked}
                          />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
                <ErrorMessage message={form.formState.errors.audiences?.[index]?.message} />
              </div>
            ))}
            <ErrorMessage message={form.formState.errors.audiences?.message} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b border-palette-jungleGreen100 pb-4">
            <CardTitle className="text-lg text-palette-jungleGreen900">App Comms</CardTitle>
            <p className="text-xs leading-relaxed text-palette-jungleGreen600 mt-1">
              Shows on the shifts page to every Active driver whose profile matches an audience,
              except substitutes, whether or not they've had a block. Only while the incentive is
              live, between its start and end dates. The end date shows as "Ends 8th Nov" when it
              has one. Leave the title and text empty for no App Comms.
            </p>
          </CardHeader>
          <CardContent className="pt-6 grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="app_comms.title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        maxLength={APP_COMMS_MAX_LENGTHS.title}
                        disabled={readOnly}
                      />
                    </FormControl>
                    <FormDescription className="text-xs leading-relaxed text-palette-jungleGreen600">
                      Up to {APP_COMMS_MAX_LENGTHS.title} characters, e.g. "5/6 Block Incentive".
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="app_comms.text"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Text</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={3}
                        maxLength={APP_COMMS_MAX_LENGTHS.text}
                        disabled={readOnly}
                        className="min-h-24 resize-y"
                      />
                    </FormControl>
                    <FormDescription className="text-xs leading-relaxed text-palette-jungleGreen600">
                      Up to {APP_COMMS_MAX_LENGTHS.text} characters.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="app_comms.terms"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Terms</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={4}
                        maxLength={APP_COMMS_MAX_LENGTHS.terms}
                        disabled={readOnly || !withAppComms}
                        className="min-h-28 resize-y"
                      />
                    </FormControl>
                    <FormDescription className="text-xs leading-relaxed text-palette-jungleGreen600">
                      Optional, once there's a title and text.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="app_comms.show_homepage_banner"
                render={({ field }) => (
                  <FormItem className="flex items-start gap-3 space-y-0">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={readOnly || !withAppComms}
                        className="mt-0.5"
                      />
                    </FormControl>
                    <div className="space-y-1">
                      <FormLabel>Show as homepage banner</FormLabel>
                      <FormDescription className="text-xs leading-relaxed text-palette-jungleGreen600">
                        Also shows it in the homepage carousel, alongside the driver's other
                        incentives with the banner on, soonest to end first.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
              <ErrorMessage message={form.formState.errors.app_comms?.message} />
            </div>
            <AppCommsPreview values={values} />
          </CardContent>
        </Card>

        {children}
      </form>
    </Form>
  )
}

function InfoTooltip({ text, className }: { text: string; className?: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger
          type="button"
          aria-label={text}
          className={cn(
            "ml-0.5 cursor-help text-palette-jungleGreen500 transition-colors hover:text-palette-jungleGreen900",
            className,
          )}
        >
          <Icon name="infoCircle" className="h-4 w-4 text-current" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
